import {ContentTile, ContentTileEmptyText} from '#/atoms/content-tile';
import {LoadingDots} from '#/atoms/dots-loading-indicator';
import {
  transactionActivityTileProps,
  transactionTypeConstants,
} from '#/components/transaction/transaction-type-constants';
import {WalletContext} from '#/contexts/wallet-provider';
import {ShareLandMarketContract} from '#/contracts/ShareLandMarketContract';
import {useInvalidateQueries} from '#/hooks/query/base/use-invalidate-queries';
import {useLandTokenContract} from '#/hooks/query/use-land-token-contract';
import {useLimitOrders} from '#/hooks/query/use-limit-orders';
import {useLimitOrdersIds} from '#/hooks/query/use-limit-orders-ids';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import useERC20Contract from '#/hooks/use-erc20-contract';
import {minimumTransactionFees} from '#/utils/contract-consts';
import {wagmiConfig} from '#/wagmi/wagmi-config';
import {FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DisplayableTransactionType, Land} from 'shareland-common';
import {Hex} from 'viem';
import {waitForTransactionReceipt} from 'wagmi/actions';

export const LimitOrdersList: FunctionComponent<{land: Land}> = ({land}) => {
  const {
    accountResult: {address: account},
  } = useContext(WalletContext);

  const {t} = useTranslation('translation');
  const shareLandMarketContract = useLandTokenContract(land);
  const {data: ordersIds = [], isLoading} = useLimitOrdersIds(account, shareLandMarketContract);
  const orders = useLimitOrders(ordersIds, shareLandMarketContract);

  if (isLoading) {
    return (
      <ContentTile className='grow py-10 flex-center'>
        <LoadingDots />
      </ContentTile>
    );
  }

  if (!orders.length) {
    return (
      <ContentTile className='grow py-10 flex-center'>
        <ContentTileEmptyText>{t('pages.actionMenu.trade.sections.orders.empty.message')}</ContentTileEmptyText>
      </ContentTile>
    );
  }

  return (
    <div className='flex flex-col gap-5'>
      {orders.map((orderQuery, index) => {
        const {data: order} = orderQuery;

        if (!!order) {
          return (
            <PortfolioLimitOrderTile
              key={index}
              land={land!}
              shareLandMarket={shareLandMarketContract!}
              order={order}
            />
          );
        } else {
          return (
            <div
              key={index}
              className={
                'bg-gray-400 text-sm  font-bold flex justify-between items-center rounded-xl p-4 animate-pulse'
              }
            >
              <div className={'grow'}>
                <span className={'block'}>&nbsp;</span>
                <span className={'block'}>&nbsp;</span>
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

const PortfolioLimitOrderTile: FunctionComponent<{
  land: Land;
  shareLandMarket: ShareLandMarketContract;
  order: {
    id: bigint;
    owner: Hex;
    landTokenAmount: bigint;
    stableTokenAmount: bigint;
    orderType: number;
  };
}> = ({land, shareLandMarket, order}) => {
  const {t} = useTranslation('translation');
  const displayPref = useCoinDisplayPreferences();
  const {
    accountResult: {address},
  } = useContext(WalletContext);

  const invalidateQueries = useInvalidateQueries();
  const erc20Contract = useERC20Contract();

  const [isCancelOrderError, setIsCancelOrderError] = useState(false);
  const [isCancelOrderLoading, setIsCancelOrderLoading] = useState(false);

  const cancelLimitOrder = async (orderId: number) => {
    setIsCancelOrderLoading(true);

    if (!shareLandMarket) return;

    try {
      const approveHash = await erc20Contract!.approve(
        shareLandMarket!.land.contractAddresses.orderBook,
        minimumTransactionFees,
      );
      if (!!approveHash) {
        if (!(await waitForTransactionReceipt(wagmiConfig, {hash: approveHash}))) {
          throw new Error('Transaction failed');
        }
      }

      const operationHash = await shareLandMarket?.cancelOrder(orderId);

      const transactionReceipt = await waitForTransactionReceipt(wagmiConfig, {
        hash: operationHash,
      });

      if (!transactionReceipt) throw new Error('Transaction failed');

      invalidateQueries.useLandMarketInfos(land.id);
      invalidateQueries.useLandAccountInfos(address, land.id);
      invalidateQueries.useStableTokenBalance();
      invalidateQueries.useLimitOrdersIds(land.id);
      invalidateQueries.useLimitOrders(land.id);
    } catch (error) {
      setIsCancelOrderError(true);
    }

    setIsCancelOrderLoading(false);
  };

  const transactionType: DisplayableTransactionType = Number(order.orderType);

  const {i18nKey} = transactionTypeConstants[transactionType];
  const {IconComponent, iconColorClassName} = transactionActivityTileProps[transactionType];

  return (
    <div
      className={'bg-graybluedark text-sm font-bold flex gap-3 flex-wrap justify-between items-stretch rounded-xl p-4'}
    >
      <IconComponent className={`${iconColorClassName} self-center min-w-fit w-6 h-6`} />

      <div className={'grow space-y-1'}>
        <span className={'block text-white whitespace-nowrap'}>
          {t('components.limitOrderTile.title', {
            transactionTypeName: t(`transactions.${i18nKey}.present`),
            landTokenSymbol: land?.tokenSymbol ?? '',
          })}
        </span>
        <span className={'block text-gray-400 whitespace-nowrap'}>
          {t('components.limitOrderTile.amount', {
            landTokenAmount: Number(order.landTokenAmount),
            stableCurrencySymbol: displayPref.symbol,
            stableTokenAmount: displayPref.displayAmount(Number(order.stableTokenAmount)),
          })}
        </span>
      </div>

      <button
        className='text-white bg-deepblue rounded p-2 self-center whitespace-nowrap disabled:bg-gray-400 disabled:cursor-not-allowed disabled:text-gray-800'
        onClick={() => cancelLimitOrder(Number(order.id))}
        disabled={isCancelOrderLoading}
      >
        {isCancelOrderLoading
          ? t('components.limitOrderTile.ctaCancelButtonLoading')
          : isCancelOrderError
          ? t('components.limitOrderTile.ctaCancelButtonError')
          : t('components.limitOrderTile.ctaCancelButtonIdle')}
      </button>

      {isCancelOrderError && (
        <div className='grow bg-downred/30 text-downred rounded-lg px-2 py-1 flex-center'>
          {t('components.limitOrderTile.ctaCancelErrorMessage')}{' '}
        </div>
      )}
    </div>
  );
};
